/**
 * CSV Import Service
 * Parse and import Hevy CSV workouts data
 */

import Papa from 'papaparse'
import { parse as parseDate, format } from 'date-fns'
import { parseExerciseName, inferMuscleGroup } from '../utils/exercise-parser.js'
import { saveWorkout, saveExercise, getWorkouts, getExerciseLibrary, saveExerciseToLibrary } from './db.js'
import { clearAndReplaceWorkouts } from './api.js'

/**
 * Parse CSV file
 * @param {File} file - CSV file
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<Array>} - Parsed rows
 */
export async function parseCSVFile(file, onProgress) {
  return new Promise((resolve, reject) => {
    const rows = []

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      step: (result) => {
        if (result.data) {
          rows.push(result.data)
        }
      },
      complete: () => {
        if (onProgress) onProgress(100)
        resolve(rows)
      },
      error: (error) => {
        reject(new Error(`CSV parsing failed: ${error.message}`))
      }
    })
  })
}

/**
 * Validate Hevy CSV format
 * @param {Array} rows - Parsed CSV rows
 * @returns {Object} - { valid: boolean, errors: Array }
 */
export function validateHevyCSV(rows) {
  const errors = []

  if (!rows || rows.length === 0) {
    errors.push('CSV file is empty')
    return { valid: false, errors }
  }

  // Check required columns
  const requiredColumns = [
    'title',
    'start_time',
    'end_time',
    'exercise_title',
    'set_index',
    'weight_kg',
    'reps'
  ]

  const firstRow = rows[0]
  const missingColumns = requiredColumns.filter(col => !(col in firstRow))

  if (missingColumns.length > 0) {
    errors.push(`Missing required columns: ${missingColumns.join(', ')}`)
    return { valid: false, errors }
  }

  return { valid: true, errors: [] }
}

/**
 * Group CSV rows by workout
 * @param {Array} rows - CSV rows
 * @returns {Array} - Grouped workouts
 */
export function groupByWorkout(rows) {
  const workoutMap = new Map()

  rows.forEach(row => {
    // Use title + start_time as unique key
    const key = `${row.title}|${row.start_time}`

    if (!workoutMap.has(key)) {
      workoutMap.set(key, {
        title: row.title,
        start_time: row.start_time,
        end_time: row.end_time,
        description: row.description || '',
        exercises: new Map()
      })
    }

    const workout = workoutMap.get(key)

    // Group by exercise_title
    const exerciseTitle = row.exercise_title
    if (!workout.exercises.has(exerciseTitle)) {
      workout.exercises.set(exerciseTitle, {
        title: exerciseTitle,
        sets: []
      })
    }

    const exercise = workout.exercises.get(exerciseTitle)

    // Add set
    exercise.sets.push({
      index: parseInt(row.set_index) || 0,
      type: row.set_type || 'normal',
      weight: parseFloat(row.weight_kg) || 0,
      reps: parseInt(row.reps) || 0,
      duration: parseInt(row.duration_seconds) || null,
      distance: parseFloat(row.distance_km) || null,
      rpe: parseFloat(row.rpe) || null
    })
  })

  // Convert Map to Array
  return Array.from(workoutMap.values()).map(workout => ({
    ...workout,
    exercises: Array.from(workout.exercises.values())
  }))
}

/**
 * Transform Hevy workout to Wild Train format
 * @param {Object} heavyWorkout - Hevy workout data
 * @returns {Object} - Wild Train workout format
 */
export function transformWorkout(heavyWorkout) {
  // Parse dates
  // Hevy format: "19 Nov 2025, 12:21"
  const dateFormat = 'd MMM yyyy, HH:mm'
  const startDate = parseDate(heavyWorkout.start_time, dateFormat, new Date())
  const endDate = parseDate(heavyWorkout.end_time, dateFormat, new Date())

  const duration = Math.floor((endDate - startDate) / 1000) // seconds

  return {
    id: crypto.randomUUID(),
    date: format(startDate, 'yyyy-MM-dd'),
    title: heavyWorkout.title,
    type: 'strength',  // Default to strength training for imported workouts
    duration: duration > 0 ? duration : null,
    notes: heavyWorkout.description || '',
    imported_from: 'hevy',
    synced: false,
    createdAt: new Date().toISOString()
  }
}

/**
 * Transform Hevy exercise to Wild Train format
 * @param {Object} heavyExercise - Hevy exercise data
 * @param {string} workoutId - Parent workout ID
 * @returns {Object} - Wild Train exercise format
 */
export function transformExercise(heavyExercise, workoutId) {
  // Parse exercise name and equipment
  const { name, equipment_type } = parseExerciseName(heavyExercise.title)

  // Infer muscle group from exercise name
  const muscleGroup = inferMuscleGroup(name)

  // Sort sets by index
  const sortedSets = heavyExercise.sets.sort((a, b) => a.index - b.index)

  // Transform sets
  const sets = sortedSets.map(set => ({
    reps: set.reps,
    weight: set.weight,
    type: set.type,
    duration: set.duration,
    distance: set.distance,
    rpe: set.rpe
  }))

  // Extract first set values for backward compatibility
  const firstSet = sets[0] || { reps: 0, weight: 0 }

  return {
    id: crypto.randomUUID(),
    workout_id: workoutId,
    exercise_name: name,  // Correct field name for workout exercises
    sets,
    reps: firstSet.reps,  // For backward compatibility
    weight: firstSet.weight,  // For backward compatibility
    rest_time: 90,  // Default rest time in seconds
    synced: false,
    createdAt: new Date().toISOString()
  }
}

/**
 * Generate workout hash for duplicate detection
 * @param {Object} workout - Workout data
 * @returns {string} - Hash string
 */
export function generateWorkoutHash(workout) {
  // Simple hash based on title + date + exercise count
  const key = `${workout.title}|${workout.date}|${workout.exercises?.length || 0}`
  return cyrb53(key)
}

/**
 * Fast non-cryptographic hash function
 * @param {string} str - String to hash
 * @param {number} seed - Seed value
 * @returns {string} - Hash string
 */
function cyrb53(str, seed = 0) {
  let h1 = 0xdeadbeef ^ seed
  let h2 = 0x41c6ce57 ^ seed

  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }

  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909)

  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16)
}

/**
 * Check if workout is duplicate
 * @param {Object} workout - Workout to check
 * @param {Array} existingWorkouts - Existing workouts
 * @returns {boolean} - True if duplicate
 */
export function isDuplicateWorkout(workout, existingWorkouts) {
  const hash = generateWorkoutHash(workout)

  return existingWorkouts.some(existing => {
    const existingHash = generateWorkoutHash(existing)
    return hash === existingHash
  })
}

/**
 * Import workouts from Hevy CSV
 * @param {File} file - CSV file
 * @param {Object} options - Import options
 * @param {Function} options.onProgress - Progress callback
 * @param {Function} options.onValidate - Validation callback
 * @param {boolean} options.skipDuplicates - Skip duplicate workouts
 * @returns {Promise<Object>} - Import result
 */
export async function importHevyCSV(file, options = {}) {
  const {
    onProgress,
    onValidate,
    skipDuplicates = true
  } = options

  try {
    // Step 1: Parse CSV
    if (onProgress) onProgress({ stage: 'parsing', progress: 0 })
    const rows = await parseCSVFile(file, (progress) => {
      if (onProgress) onProgress({ stage: 'parsing', progress: progress * 0.2 })
    })

    // Step 2: Validate
    if (onProgress) onProgress({ stage: 'validating', progress: 20 })
    const validation = validateHevyCSV(rows)

    if (!validation.valid) {
      throw new Error(`Invalid CSV format: ${validation.errors.join(', ')}`)
    }

    // Step 3: Group by workout
    if (onProgress) onProgress({ stage: 'grouping', progress: 30 })
    const groupedWorkouts = groupByWorkout(rows)

    // Step 4: Transform to Wild Train format
    if (onProgress) onProgress({ stage: 'transforming', progress: 40 })
    const transformedWorkouts = groupedWorkouts.map(hw => {
      const workout = transformWorkout(hw)
      const exercises = hw.exercises.map(ex => transformExercise(ex, workout.id))
      return { ...workout, exercises }
    })

    // Step 5: Validation callback (for preview)
    if (onValidate) {
      const shouldContinue = await onValidate(transformedWorkouts)
      if (!shouldContinue) {
        return {
          success: false,
          cancelled: true,
          message: 'Import cancelled by user'
        }
      }
    }

    // Step 6: Check duplicates
    if (skipDuplicates) {
      if (onProgress) onProgress({ stage: 'checking_duplicates', progress: 50 })
      const existingWorkouts = await getWorkouts()
      const uniqueWorkouts = transformedWorkouts.filter(w =>
        !isDuplicateWorkout(w, existingWorkouts)
      )

      if (uniqueWorkouts.length === 0) {
        return {
          success: false,
          message: 'All workouts already exist (duplicates detected)',
          skipped: transformedWorkouts.length
        }
      }

      // Use unique workouts only
      transformedWorkouts.splice(0, transformedWorkouts.length, ...uniqueWorkouts)
    }

    // Step 7: Extract unique exercises for Exercise Library
    if (onProgress) onProgress({ stage: 'extracting_exercises', progress: 60 })

    const uniqueExerciseNames = new Set()
    const exercisesToAdd = []

    transformedWorkouts.forEach(workout => {
      workout.exercises.forEach(exercise => {
        const exerciseName = exercise.exercise_name
        if (!uniqueExerciseNames.has(exerciseName)) {
          uniqueExerciseNames.add(exerciseName)

          // Parse exercise details
          const { name, equipment_type } = parseExerciseName(exerciseName)
          const muscleGroup = inferMuscleGroup(name)

          exercisesToAdd.push({
            id: crypto.randomUUID(),
            name: exerciseName,
            equipment_type: equipment_type || null,
            muscleGroup: muscleGroup,
            description: '',
            isCustom: true,  // Imported exercises are custom
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          })
        }
      })
    })

    // Check against existing exercise library
    const existingLibrary = await getExerciseLibrary()
    const existingNames = new Set(existingLibrary.map(ex => ex.name))

    const newExercises = exercisesToAdd.filter(ex => !existingNames.has(ex.name))

    // Add new exercises to library
    for (const exercise of newExercises) {
      await saveExerciseToLibrary(exercise)
    }

    if (onProgress) onProgress({ stage: 'saving_exercises', progress: 70 })

    // Step 8: Use ULTRA-FAST clear-and-replace strategy
    // WARNING: This will DELETE ALL existing workouts and exercises!
    // Then batch insert all imported data in one go
    if (onProgress) onProgress({ stage: 'clearing_old_data', progress: 75 })

    try {
      // First save to IndexedDB (local storage)
      const batchSize = 50
      const totalBatches = Math.ceil(transformedWorkouts.length / batchSize)
      let saved = 0

      for (let i = 0; i < totalBatches; i++) {
        const batch = transformedWorkouts.slice(i * batchSize, (i + 1) * batchSize)

        for (const workout of batch) {
          await saveWorkout(workout)
          for (const exercise of workout.exercises) {
            await saveExercise(exercise)
          }
          saved++
        }

        const progress = 75 + (saved / transformedWorkouts.length) * 15
        if (onProgress) onProgress({ stage: 'saving_locally', progress, saved, total: transformedWorkouts.length })
      }

      // Step 9: Sync to server using clear-and-replace API (5-10x faster!)
      if (onProgress) onProgress({ stage: 'syncing_to_server', progress: 90 })

      await clearAndReplaceWorkouts(transformedWorkouts)

      if (onProgress) onProgress({ stage: 'complete', progress: 100 })
    } catch (syncError) {
      console.error('Failed to sync to server:', syncError)
      // Import to local IndexedDB succeeded, but server sync failed
      // User can manually sync later
      if (onProgress) onProgress({ stage: 'complete', progress: 100 })
    }

    return {
      success: true,
      imported: transformedWorkouts.length,
      skipped: skipDuplicates ? (groupedWorkouts.length - transformedWorkouts.length) : 0,
      exercisesAdded: newExercises.length,
      message: `Successfully imported ${transformedWorkouts.length} workouts and ${newExercises.length} new exercises to library`
    }
  } catch (error) {
    console.error('Import failed:', error)
    return {
      success: false,
      error: error.message
    }
  }
}
